import { dayjs } from '../Dayjs/dayjs-utils';

import type { RoomCalendarViewConfig } from '../../Types/AuthServer';

export const HOURS_START = 7;
export const HOURS_END = 24;
export const MINUTES_PER_HOUR = 60;
export const TOTAL_VISIBLE_MINUTES = (HOURS_END - HOURS_START) * MINUTES_PER_HOUR;

export interface HourRange {
    endHour: number;
    startHour: number;
}

/** The fixed 07-24 window. Still what the day views use, and the fallback for a week with no bookings. */
export const DEFAULT_HOUR_RANGE: HourRange = { endHour: HOURS_END, startHour: HOURS_START };

/** Never collapse the axis below this, so a week with one short booking does not become one huge box. */
const MIN_HOUR_RANGE_SPAN_HOURS = 6;
/** Breathing room kept above the first and below the last booking of the week. */
const HOUR_RANGE_PADDING_HOURS = 1;

export function getHourRangeMinutes(hourRange: HourRange) {
    return (hourRange.endHour - hourRange.startHour) * MINUTES_PER_HOUR;
}
export const TIME_AXIS_WIDTH = 82;
export const DAY_HEADER_HEIGHT = 50;
export const ALL_DAY_ROW_HEIGHT = 48;
/** Line height used for every text line inside an event box, shared with the fit calculation. */
export const EVENT_LINE_HEIGHT = 1.1;
/** Used until the ResizeObserver has measured the grid, so the first paint is not degenerate. */
export const FALLBACK_GRID_BODY_HEIGHT_PX = 720;

/**
 * The reservation title is never dropped, so this is the target device's own floor rather than a
 * design choice: the Android TV browser paints anything below 8px at 8px anyway, and asking for less
 * only makes the fit calculation reserve less space than the text really takes.
 */
const LABEL_MIN_FONT_SIZE_PX = 8;
/**
 * Kept close to the minimum on purpose. A title only ever reaches this in a view whose longest title
 * still fits at this size, so a bigger maximum does not buy legibility — it only breaks short names
 * mid-word to fill the width.
 */
const LABEL_MAX_FONT_SIZE_PX = 11;
const LABEL_FONT_STEP_PX = 0.5;
const LABEL_MAX_LINES = 3;
/**
 * Measured on the target Android TV stick (Chrome 150 / Android 14): the browser enforces a hard 8px
 * minimum font size, so anything requested below 8px is painted at 8px anyway. Reserving width and
 * height for a smaller time than the device can render makes the time overflow and push the title
 * out of the box.
 */
const TIME_MIN_FONT_SIZE_PX = 8;
const TIME_MAX_FONT_SIZE_PX = 10;
const TIME_FONT_STEP_PX = 0.5;
/**
 * Average glyph advance width of the bold UI font relative to its font size. Measured at 0.47 on the
 * target device; kept slightly above that as a safety margin for wide glyphs. Over-estimating this
 * makes the wrap calculation invent line breaks and truncation that the browser never performs.
 */
const GLYPH_WIDTH_RATIO = 0.49;
/**
 * The time is all digits, and the probe measured its 0.47 on a letter sample ('Matti Meikalainen').
 * Digits carry a wider advance than that letter average in the bold UI font, and the fallback font the
 * TV picks when SF Pro is missing is wider still. Estimating `00:00-00:00` at the letter ratio made it
 * come out at exactly the lane width with no slack, so the box declared a fit and the browser then
 * ellipsised it to `15:30-17:..` on the wall.
 */
const TIME_GLYPH_WIDTH_RATIO = 0.55;
/**
 * Horizontal / vertical chrome of an event box: padding plus border. Both are 6px, and that is what
 * the box really paints (`px: 0.25` / `py: 0.25` on the inner Box is 2px a side, plus a 1px border a
 * side). Over-stating the horizontal chrome costs two characters of every line, which is exactly the
 * margin by which `00:00-00:00` failed to fit in a three-lane column and fell back to the bare start
 * time.
 */
const EVENT_HORIZONTAL_PADDING_PX = 6;
const EVENT_VERTICAL_PADDING_PX = 6;
const WIDEST_TIME_RANGE_LABEL = '00:00-00:00';
/** A range that does not fit one line breaks after the hyphen, so two lines is the worst case. */
const MAX_TIME_LINES = 2;
/**
 * A short booking may outgrow its own slot so that its title and time both still fit — a 30-minute
 * box is only ~21px tall, well below the three 9px title lines plus a time line a long name needs.
 * This bounds the height budget the font search may assume; the box itself grows to whatever the
 * chosen fonts really paint (CSS `min-height: fit-content`), never to this cap.
 */
const MAX_GROWN_EVENT_HEIGHT_PX = 60;
const LINE_FIT_TOLERANCE_PX = 0.5;

export interface RoomCalendarEvent {
    all_day?: boolean;
    description?: string;
    end: string;
    id: string;
    start: string;
    title: string;
}

export interface PositionedEvent {
    endMinutes: number;
    id: string;
    lane: number;
    laneCount: number;
    publicLabel: string;
    startMinutes: number;
}

export interface DayColumnData {
    allDayEvents: string[];
    positionedEvents: PositionedEvent[];
}

export interface TimedEventLayout {
    gapPx: number;
    innerInsetPx: number;
    labelFontSizePx: number;
    labelLineClamp: number;
    timeFontSizePx: number;
    timeLineCount: number;
    verticalInsetPx: number;
}

/** Configured rooms, newest multi-room field first and the legacy single-room field as fallback. */
export function getConfiguredRoomGroupIds(roomCalendarConfig?: RoomCalendarViewConfig): string[] {
    const configuredIds = [...(roomCalendarConfig?.socialGroupIds ?? []), roomCalendarConfig?.socialGroupId ?? ''];

    return [...new Set(configuredIds.map((groupId) => String(groupId ?? '')).filter(Boolean))];
}

export function getWeekStart(now: dayjs.Dayjs) {
    const dayOfWeek = now.day();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return now.subtract(mondayOffset, 'day').startOf('day');
}

export function isWeekendDay(day: dayjs.Dayjs) {
    const dayOfWeek = day.day();
    return dayOfWeek === 0 || dayOfWeek === 6;
}

export function getVisibleTimeLabel(hour: number) {
    return `${hour.toString().padStart(2, '0')}:00`;
}

export function getHourLinePercentage(hour: number, hourRange: HourRange = DEFAULT_HOUR_RANGE) {
    return ((hour - hourRange.startHour) * MINUTES_PER_HOUR * 100) / getHourRangeMinutes(hourRange);
}

export function formatCompactEventTimeRange(start: dayjs.Dayjs, end: dayjs.Dayjs) {
    return `${start.format('HH:mm')}-${end.format('HH:mm')}`;
}

export function getPrivateEventLabel(isAllDay: boolean) {
    return isAllDay ? 'Reserved all day' : 'Reserved';
}

export function clampMinutes(value: number, hourRange: HourRange = DEFAULT_HOUR_RANGE) {
    return Math.min(Math.max(value, 0), getHourRangeMinutes(hourRange));
}

export function clampNumber(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

/**
 * How many wrapped lines a text needs at a given font size, without touching the DOM: words are
 * packed greedily like the browser does, and a word longer than the line breaks across lines
 * (the label styles use `overflow-wrap: anywhere`, so that is what actually happens on screen).
 */
export function estimateWrappedLineCount({
    availableWidthPx,
    fontSizePx,
    glyphWidthRatio = GLYPH_WIDTH_RATIO,
    text,
}: {
    availableWidthPx: number;
    fontSizePx: number;
    glyphWidthRatio?: number;
    text: string;
}): number {
    const maxCharsPerLine = Math.max(Math.floor(availableWidthPx / (fontSizePx * glyphWidthRatio)), 1);
    const words = text.trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) {
        return 1;
    }

    let lineCount = 1;
    let currentLineLength = 0;

    for (const word of words) {
        const fitsOnCurrentLine =
            currentLineLength > 0 && currentLineLength + 1 + word.length <= maxCharsPerLine;

        if (fitsOnCurrentLine) {
            currentLineLength += 1 + word.length;
            continue;
        }

        if (currentLineLength > 0) {
            lineCount += 1;
        }

        const extraLinesFromBrokenWord = Math.ceil(word.length / maxCharsPerLine) - 1;
        lineCount += extraLinesFromBrokenWord;
        currentLineLength = word.length - extraLinesFromBrokenWord * maxCharsPerLine;
    }

    return lineCount;
}

/** Geometry of one event box, as the caller has already laid it out on the grid. */
export interface EventBoxMetrics {
    eventHeightPx: number;
    eventWidthPx: number;
    label: string;
}

/** Font sizes shared by every event box of one view. */
export interface EventTypography {
    gapPx: number;
    labelFontSizePx: number;
    timeFontSizePx: number;
}

function getConstrainedEventWidthPx(eventWidthPx: number) {
    return clampNumber(eventWidthPx, 42, 360);
}

function getEventAvailableWidthPx(constrainedEventWidthPx: number) {
    return Math.max(constrainedEventWidthPx - EVENT_HORIZONTAL_PADDING_PX, 16);
}

/**
 * Lines the full `HH:mm-HH:mm` range takes in one box. The end time is never dropped: a lane too
 * narrow for the range at the device's 8px floor breaks it after the hyphen instead, because a
 * reservation whose end nobody can read is the one thing a room screen must not do.
 */
function getTimeLineCount({ availableWidthPx, timeFontSizePx }: { availableWidthPx: number; timeFontSizePx: number }) {
    return Math.min(
        estimateWrappedLineCount({
            availableWidthPx,
            fontSizePx: timeFontSizePx,
            glyphWidthRatio: TIME_GLYPH_WIDTH_RATIO,
            text: WIDEST_TIME_RANGE_LABEL,
        }),
        MAX_TIME_LINES,
    );
}

/**
 * Height the title may use inside one box, once the time line has taken its own share. The time is
 * reserved space rather than leftover space: a viewer walking past a TV needs both lines, so the
 * title is sized against the box minus the time, never the other way round.
 */
function getLabelAvailableHeightPx({
    availableWidthPx,
    eventHeightPx,
    gapPx,
    label,
    timeFontSizePx,
}: {
    availableWidthPx: number;
    eventHeightPx: number;
    gapPx: number;
    label: string;
    timeFontSizePx: number;
}) {
    const timeLineCount = getTimeLineCount({ availableWidthPx, timeFontSizePx });
    const reservedTimeHeightPx = timeFontSizePx * EVENT_LINE_HEIGHT * timeLineCount + gapPx;
    // A 30-minute booking is only ~25px tall, which is not enough for a wrapped name plus a time.
    // Let such a box grow past its own slot instead of cutting either, bounded so it cannot swallow
    // the following hours.
    const linesNeededAtMinFontSize = Math.min(
        estimateWrappedLineCount({ availableWidthPx, fontSizePx: LABEL_MIN_FONT_SIZE_PX, text: label }),
        LABEL_MAX_LINES,
    );
    const heightNeededPx =
        linesNeededAtMinFontSize * LABEL_MIN_FONT_SIZE_PX * EVENT_LINE_HEIGHT +
        reservedTimeHeightPx +
        EVENT_VERTICAL_PADDING_PX;
    const grownEventHeightPx = Math.max(eventHeightPx, Math.min(heightNeededPx, MAX_GROWN_EVENT_HEIGHT_PX));
    const availableHeightPx = Math.max(grownEventHeightPx - EVENT_VERTICAL_PADDING_PX, 10);

    // Never let the time eat the last line: the title keeps at least one minimum-size line even in a
    // box that is too short for both.
    return Math.max(availableHeightPx - reservedTimeHeightPx, LABEL_MIN_FONT_SIZE_PX * EVENT_LINE_HEIGHT);
}

function getLabelLinesThatFit({ fontSizePx, labelAvailableHeightPx }: { fontSizePx: number; labelAvailableHeightPx: number }) {
    // Half a pixel of slack, because these heights are fractions of a percentage of a measured grid:
    // a line that needs exactly the height available otherwise loses to binary rounding and the box
    // clamps away a line it really has room for.
    return Math.min(
        Math.floor((labelAvailableHeightPx + LINE_FIT_TOLERANCE_PX) / (fontSizePx * EVENT_LINE_HEIGHT)),
        LABEL_MAX_LINES,
    );
}

/**
 * One title size and one time size for a whole view, rather than a per-box maximum.
 *
 * Sizing each box on its own looks broken on the wall: the search picks the largest size at which
 * that one title still fits, so a short name grows to the maximum and breaks mid-word while a long
 * name in the neighbouring lane drops to the minimum. Two reservations side by side then carry
 * visibly different typography, and the bigger one is the one with less to say.
 *
 * So the size is the largest at which *every* event of the view fits its own box. A view whose
 * longest title needs the minimum renders all titles at the minimum — uniform, and no worse for any
 * single box than the per-box search was, since that box was going to be sized down anyway.
 *
 * The time is sized first, from the narrowest lane in the view, and wraps onto a second line where
 * the full range cannot fit on one even at the device's 8px floor.
 */
export function getUniformEventTypography(events: readonly EventBoxMetrics[]): EventTypography {
    const constrainedWidthsPx = events.map((event) => getConstrainedEventWidthPx(event.eventWidthPx));
    const narrowestConstrainedWidthPx = Math.min(...constrainedWidthsPx, 360);
    const narrowestAvailableWidthPx = getEventAvailableWidthPx(narrowestConstrainedWidthPx);
    const gapPx = narrowestConstrainedWidthPx <= 110 ? 1 : 2;

    let timeFontSizePx = TIME_MIN_FONT_SIZE_PX;

    for (
        let candidateFontSizePx = TIME_MAX_FONT_SIZE_PX;
        candidateFontSizePx > TIME_MIN_FONT_SIZE_PX;
        candidateFontSizePx -= TIME_FONT_STEP_PX
    ) {
        if (
            getTimeLineCount({
                availableWidthPx: narrowestAvailableWidthPx,
                timeFontSizePx: candidateFontSizePx,
            }) === 1
        ) {
            timeFontSizePx = candidateFontSizePx;
            break;
        }
    }

    // No compact fallback below this: 8px is the device's own floor, so a lane still too narrow for
    // the range wraps it onto a second line rather than losing the end time.
    const labelBudgets = events.map((event, index) => {
        const availableWidthPx = getEventAvailableWidthPx(constrainedWidthsPx[index] ?? narrowestConstrainedWidthPx);

        return {
            availableWidthPx,
            labelAvailableHeightPx: getLabelAvailableHeightPx({
                availableWidthPx,
                eventHeightPx: event.eventHeightPx,
                gapPx,
                label: event.label,
                timeFontSizePx,
            }),
            label: event.label,
        };
    });

    let labelFontSizePx = LABEL_MIN_FONT_SIZE_PX;

    for (
        let candidateFontSizePx = LABEL_MAX_FONT_SIZE_PX;
        candidateFontSizePx > LABEL_MIN_FONT_SIZE_PX;
        candidateFontSizePx -= LABEL_FONT_STEP_PX
    ) {
        const everyLabelFits = labelBudgets.every((budget) => {
            const linesThatFit = getLabelLinesThatFit({
                fontSizePx: candidateFontSizePx,
                labelAvailableHeightPx: budget.labelAvailableHeightPx,
            });

            if (linesThatFit < 1) {
                return false;
            }

            return (
                estimateWrappedLineCount({
                    availableWidthPx: budget.availableWidthPx,
                    fontSizePx: candidateFontSizePx,
                    text: budget.label,
                }) <= linesThatFit
            );
        });

        if (everyLabelFits) {
            labelFontSizePx = candidateFontSizePx;
            break;
        }
    }

    // The time is sized from the narrowest lane alone, the title from every box, so one tight box drags
    // the titles down while the time keeps its own maximum — a 10px time over an 8px name, which reads
    // as the wrong thing being emphasised. The reservation's name is the primary text, so the time is
    // capped at it. Shrinking the time only frees height, so the title fit found above still holds.
    const cappedTimeFontSizePx = Math.min(timeFontSizePx, labelFontSizePx);

    return { gapPx, labelFontSizePx, timeFontSizePx: cappedTimeFontSizePx };
}

/**
 * Layout of one event box at the view's shared typography. Callers that lay out several boxes pass
 * the `typography` from `getUniformEventTypography` over all of them; a caller that omits it gets
 * that same search run over this box alone.
 *
 * Only the font sizes and the line clamp are computed here. The rendered height is left to the
 * browser (`min-height: fit-content` on the event box), because the wrap estimate is deliberately
 * pessimistic — a title the estimate splits over three lines but the browser fits on two would
 * otherwise leave the extra line as empty space under the time.
 */
export function getTimedEventLayout({
    eventHeightPx,
    eventWidthPx,
    label,
    typography,
}: {
    eventHeightPx: number;
    eventWidthPx: number;
    label: string;
    typography?: EventTypography;
}): TimedEventLayout {
    const constrainedEventWidthPx = getConstrainedEventWidthPx(eventWidthPx);
    const availableWidthPx = getEventAvailableWidthPx(constrainedEventWidthPx);
    const { gapPx, labelFontSizePx, timeFontSizePx } =
        typography ?? getUniformEventTypography([{ eventHeightPx, eventWidthPx, label }]);
    const labelAvailableHeightPx = getLabelAvailableHeightPx({
        availableWidthPx,
        eventHeightPx,
        gapPx,
        label,
        timeFontSizePx,
    });
    const linesThatFit = Math.max(getLabelLinesThatFit({ fontSizePx: labelFontSizePx, labelAvailableHeightPx }), 1);
    const linesNeeded = estimateWrappedLineCount({ availableWidthPx, fontSizePx: labelFontSizePx, text: label });

    return {
        gapPx,
        innerInsetPx: constrainedEventWidthPx >= 220 ? 2 : constrainedEventWidthPx >= 150 ? 1 : 0,
        labelFontSizePx,
        labelLineClamp: clampNumber(Math.min(linesNeeded, linesThatFit), 1, LABEL_MAX_LINES),
        timeFontSizePx,
        timeLineCount: getTimeLineCount({ availableWidthPx, timeFontSizePx }),
        verticalInsetPx: 0,
    };
}

/**
 * Public label for a reservation on a screen anyone can walk past: the free-form event title when
 * the slide is configured to show titles, otherwise a neutral 'Reserved' placeholder.
 */
export function getEventPublicLabel({
    event,
    showEventTitles,
}: {
    event: RoomCalendarEvent;
    showEventTitles: boolean;
}): string {
    const title = showEventTitles ? event.title.trim() : '';

    return title || getPrivateEventLabel(Boolean(event.all_day));
}

export function getDayColumnData({
    currentDay,
    events,
    showEventTitles = false,
    timezone,
}: {
    currentDay: dayjs.Dayjs;
    events: RoomCalendarEvent[];
    showEventTitles?: boolean;
    timezone: string;
}): DayColumnData {
    const dayStart = currentDay.startOf('day');
    const dayEnd = currentDay.endOf('day');
    const visibleStart = currentDay.hour(HOURS_START).minute(0).second(0).millisecond(0);
    const visibleEnd = currentDay.endOf('day');
    const allDayEvents: string[] = [];
    const rawPositionedEvents: Omit<PositionedEvent, 'lane' | 'laneCount'>[] = [];

    for (const event of events) {
        const eventStart = dayjs(event.start).tz(timezone);
        const eventEnd = dayjs(event.end).tz(timezone);
        const publicLabel = getEventPublicLabel({ event, showEventTitles });

        if (event.all_day) {
            if (eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart)) {
                allDayEvents.push(publicLabel);
            }
            continue;
        }

        if (!eventEnd.isAfter(dayStart) || !eventStart.isBefore(dayEnd)) {
            continue;
        }

        const segmentStart = eventStart.isAfter(visibleStart) ? eventStart : visibleStart;
        const segmentEnd = eventEnd.isBefore(visibleEnd) ? eventEnd : visibleEnd;

        if (!segmentEnd.isAfter(segmentStart)) {
            continue;
        }

        rawPositionedEvents.push({
            endMinutes: clampMinutes(segmentEnd.diff(visibleStart, 'minute')),
            id: event.id,
            publicLabel,
            startMinutes: clampMinutes(segmentStart.diff(visibleStart, 'minute')),
        });
    }

    rawPositionedEvents.sort((firstEvent, secondEvent) => {
        if (firstEvent.startMinutes === secondEvent.startMinutes) {
            return firstEvent.endMinutes - secondEvent.endMinutes;
        }
        return firstEvent.startMinutes - secondEvent.startMinutes;
    });

    const laneEndTimes: number[] = [];
    const positionedEvents: PositionedEvent[] = rawPositionedEvents.map((event) => {
        let lane = laneEndTimes.findIndex((laneEnd) => laneEnd <= event.startMinutes);

        if (lane === -1) {
            lane = laneEndTimes.length;
            laneEndTimes.push(event.endMinutes);
        } else {
            laneEndTimes[lane] = event.endMinutes;
        }

        return {
            ...event,
            lane,
            laneCount: 1,
        };
    });

    const laneCount = Math.max(1, laneEndTimes.length);

    return {
        allDayEvents,
        positionedEvents: positionedEvents.map((event) => ({
            ...event,
            laneCount,
        })),
    };
}

export interface MultiRoomAllDayEvent {
    label: string;
    roomColor: string;
    roomLabel: string;
}

export interface MultiRoomPositionedEvent extends PositionedEvent {
    roomColor: string;
    roomLabel: string;
}

export interface MultiRoomDayColumnData {
    allDayEvents: MultiRoomAllDayEvent[];
    positionedEvents: MultiRoomPositionedEvent[];
}

export interface RoomEventGroup {
    events: RoomCalendarEvent[];
    roomColor: string;
    roomId: string;
    roomLabel: string;
}

/**
 * Narrowest hour window that still contains every booking of the visible week, padded by an hour on
 * each side. The week grid is the same height whatever it shows, so the fixed 07-24 axis spends that
 * height on hours nobody has booked: on a 540px-tall TV viewport it leaves ~25px per hour, which is
 * less than one line of text plus a time. A typical 8-17 office week roughly doubles every box.
 */
export function getWeekHourRange({
    days,
    roomEventGroups,
    timezone,
}: {
    days: readonly dayjs.Dayjs[];
    roomEventGroups: readonly RoomEventGroup[];
    timezone: string;
}): HourRange {
    let earliestHour: number | null = null;
    let latestHour: number | null = null;

    for (const day of days) {
        const dayStart = day.startOf('day');
        const dayEnd = day.endOf('day');

        for (const { events } of roomEventGroups) {
            for (const event of events) {
                if (event.all_day) {
                    continue;
                }

                const eventStart = dayjs(event.start).tz(timezone);
                const eventEnd = dayjs(event.end).tz(timezone);

                if (!eventEnd.isAfter(dayStart) || !eventStart.isBefore(dayEnd)) {
                    continue;
                }

                // Clamped to this day, so a booking running over midnight contributes its late hours
                // to one day and its early hours to the next rather than a bogus 24-hour span.
                const segmentStart = eventStart.isAfter(dayStart) ? eventStart : dayStart;
                const segmentEnd = eventEnd.isBefore(dayEnd) ? eventEnd : dayEnd;
                const startHour = Math.floor(segmentStart.diff(dayStart, 'minute') / MINUTES_PER_HOUR);
                const endHour = Math.ceil(segmentEnd.diff(dayStart, 'minute') / MINUTES_PER_HOUR);

                earliestHour = earliestHour === null ? startHour : Math.min(earliestHour, startHour);
                latestHour = latestHour === null ? endHour : Math.max(latestHour, endHour);
            }
        }
    }

    if (earliestHour === null || latestHour === null) {
        return DEFAULT_HOUR_RANGE;
    }

    let startHour = Math.max(earliestHour - HOUR_RANGE_PADDING_HOURS, 0);
    let endHour = Math.min(latestHour + HOUR_RANGE_PADDING_HOURS, 24);

    while (endHour - startHour < MIN_HOUR_RANGE_SPAN_HOURS && (startHour > 0 || endHour < 24)) {
        if (endHour < 24) {
            endHour += 1;
        }

        if (endHour - startHour < MIN_HOUR_RANGE_SPAN_HOURS && startHour > 0) {
            startHour -= 1;
        }
    }

    return { endHour, startHour };
}

/**
 * Same segmenting/lane logic as getDayColumnData, but merges several rooms' events into one combined
 * lane assignment so overlapping bookings from different rooms land in separate lanes side by side —
 * like a normal week calendar overlaying multiple calendars, instead of one column per room.
 */
export function getMultiRoomWeekDayData({
    currentDay,
    hourRange = DEFAULT_HOUR_RANGE,
    roomEventGroups,
    showEventTitles = false,
    timezone,
}: {
    currentDay: dayjs.Dayjs;
    hourRange?: HourRange;
    roomEventGroups: readonly RoomEventGroup[];
    showEventTitles?: boolean;
    timezone: string;
}): MultiRoomDayColumnData {
    const dayStart = currentDay.startOf('day');
    const dayEnd = currentDay.endOf('day');
    const visibleStart = currentDay.hour(hourRange.startHour).minute(0).second(0).millisecond(0);
    const visibleEnd =
        hourRange.endHour >= 24
            ? currentDay.endOf('day')
            : currentDay.hour(hourRange.endHour).minute(0).second(0).millisecond(0);
    const allDayEvents: MultiRoomAllDayEvent[] = [];
    const rawPositionedEvents: Omit<MultiRoomPositionedEvent, 'lane' | 'laneCount'>[] = [];

    for (const { events, roomColor, roomId, roomLabel } of roomEventGroups) {
        for (const event of events) {
            const eventStart = dayjs(event.start).tz(timezone);
            const eventEnd = dayjs(event.end).tz(timezone);
            const publicLabel = getEventPublicLabel({ event, showEventTitles });

            if (event.all_day) {
                if (eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart)) {
                    allDayEvents.push({ label: publicLabel, roomColor, roomLabel });
                }
                continue;
            }

            if (!eventEnd.isAfter(dayStart) || !eventStart.isBefore(dayEnd)) {
                continue;
            }

            const segmentStart = eventStart.isAfter(visibleStart) ? eventStart : visibleStart;
            const segmentEnd = eventEnd.isBefore(visibleEnd) ? eventEnd : visibleEnd;

            if (!segmentEnd.isAfter(segmentStart)) {
                continue;
            }

            rawPositionedEvents.push({
                endMinutes: clampMinutes(segmentEnd.diff(visibleStart, 'minute'), hourRange),
                id: `${roomId}:${event.id}`,
                publicLabel,
                roomColor,
                roomLabel,
                startMinutes: clampMinutes(segmentStart.diff(visibleStart, 'minute'), hourRange),
            });
        }
    }

    rawPositionedEvents.sort((firstEvent, secondEvent) => {
        if (firstEvent.startMinutes === secondEvent.startMinutes) {
            return firstEvent.endMinutes - secondEvent.endMinutes;
        }
        return firstEvent.startMinutes - secondEvent.startMinutes;
    });

    // Lanes are counted per overlap cluster, not per day. A single day-wide lane count made every box
    // as narrow as the busiest moment of that day, so one triple booking at 09:00 shrank a lone 15:00
    // reservation to a third of the column for no reason. A booking that overlaps nothing now keeps
    // the whole day column, which is the difference between ~55px and ~182px of text width on the TV.
    const positionedEvents: MultiRoomPositionedEvent[] = [];
    let clusterEvents: MultiRoomPositionedEvent[] = [];
    let clusterLaneEndTimes: number[] = [];
    let clusterEndMinutes = -1;

    const flushCluster = () => {
        const clusterLaneCount = Math.max(clusterLaneEndTimes.length, 1);

        for (const event of clusterEvents) {
            positionedEvents.push({ ...event, laneCount: clusterLaneCount });
        }

        clusterEvents = [];
        clusterLaneEndTimes = [];
        clusterEndMinutes = -1;
    };

    for (const event of rawPositionedEvents) {
        // Sorted by start, so the first event starting at or after everything seen so far begins a
        // new cluster that shares no pixels with the previous one.
        if (clusterEvents.length > 0 && event.startMinutes >= clusterEndMinutes) {
            flushCluster();
        }

        let lane = clusterLaneEndTimes.findIndex((laneEnd) => laneEnd <= event.startMinutes);

        if (lane === -1) {
            lane = clusterLaneEndTimes.length;
            clusterLaneEndTimes.push(event.endMinutes);
        } else {
            clusterLaneEndTimes[lane] = event.endMinutes;
        }

        clusterEvents.push({ ...event, lane, laneCount: 1 });
        clusterEndMinutes = Math.max(clusterEndMinutes, event.endMinutes);
    }

    flushCluster();

    return {
        allDayEvents,
        positionedEvents,
    };
}
